import './AdvertisingForm.css';
import React, { useState, useRef } from 'react';
import { capture } from '../../analytics';

const initialForm = {
  email: '',
  company: '',
  campaign: 'newsletter',
  budget: '',
  notes: '',
  homepage: '',
};

function formatAdvertisingError(error) {
  if (error === 'invalid_email') return 'Use a valid work email.';
  if (error === 'email_delivery_unavailable') return 'Email is not configured on the server yet.';
  if (error === 'advertising_contact_send_failed') return 'I could not send this yet. Try again in a minute.';
  return 'Something went wrong. Try again in a minute.';
}

function AdvertisingForm({ compact = false }) {
  const [form, setForm] = useState(initialForm);
  const started = useRef(false);
  const [status, setStatus] = useState({ kind: 'idle', message: '' });

  function updateField(field, value) {
    if (!started.current && field !== 'homepage') {
      started.current = true;
      capture('f1000_inquiry_started', { release: '2026-09-18' });
    }
    setForm((current) => ({ ...current, [field]: value }));
    if (status.kind === 'error') {
      setStatus({ kind: 'idle', message: '' });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (status.kind === 'submitting') return;

    const normalizedEmail = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setStatus({ kind: 'error', message: 'Use a valid work email.' });
      return;
    }

    if (compact && !form.notes.trim()) {
      setStatus({ kind: 'error', message: 'Add a note.' });
      return;
    }

    setStatus({ kind: 'submitting', message: 'Sending your note...' });

    try {
      const response = await fetch('/api/advertising/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...form,
          campaign: compact ? 'other' : form.campaign,
          email: normalizedEmail,
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.ok !== true) {
        throw new Error(formatAdvertisingError(payload.error));
      }

      setStatus({
        kind: 'success',
        message: 'Sent. I will review it personally and reply with the cleanest next step.',
      });
      capture('f1000_inquiry_submitted', { release: '2026-09-18' });
      setForm(initialForm);
    } catch (error) {
      setStatus({
        kind: 'error',
        message: error instanceof Error ? error.message : formatAdvertisingError('unknown'),
      });
    }
  }

  return (
    <section className={`Advertising-section${compact ? ' Advertising-section--compact' : ''}`} id="advertise" aria-labelledby="advertising-heading">
      {compact ? <h2 id="advertising-heading">Send a note</h2> : null}
      <div className="Advertising-panel">
        {!compact ? <div className="Advertising-copy">
          <p className="Advertising-kicker">Advertise</p>
          <h1 id="advertising-heading">A good product.<br/>The right readers.</h1>
          <p>
            Put something useful in front of founders, product people, and growth teams. Tell me what you’re building and why it belongs in First 1000.
          </p>
          <p><a href="/advertise/ai-tool-campaign-guide" data-funnel="advertise-guide">Plan your campaign →</a></p>
          <div className="Advertising-proof" aria-label="Audience notes">
            <span>Written by Ali</span>
            <span>Founders and growth teams</span>
            <span>AI and growth focused</span>
          </div>
        </div> : null}

        <form className="Advertising-form" onSubmit={handleSubmit} noValidate>
          <label className="Advertising-honeypot">
            Website
            <input
              autoComplete="off"
              name="homepage"
              onChange={(event) => updateField('homepage', event.target.value)}
              tabIndex="-1"
              type="text"
              value={form.homepage}
            />
          </label>

          <div className="Advertising-field">
            <label htmlFor="advertising-email">{compact ? 'Email' : 'Work email'}</label>
            <input
              autoComplete="email"
              id="advertising-email"
              name="email"
              onChange={(event) => updateField('email', event.target.value)}
              placeholder="you@company.com"
              type="email"
              value={form.email}
            />
          </div>

          {!compact ? <><div className="Advertising-field">
            <label htmlFor="advertising-company">Company or product</label>
            <input
              autoComplete="organization"
              id="advertising-company"
              name="company"
              onChange={(event) => updateField('company', event.target.value)}
              placeholder="Company name"
              type="text"
              value={form.company}
            />
          </div>

          <div className="Advertising-field Advertising-field--split">
            <div>
              <label htmlFor="advertising-campaign">Campaign type</label>
              <select
                id="advertising-campaign"
                name="campaign"
                onChange={(event) => updateField('campaign', event.target.value)}
                value={form.campaign}
              >
                <option value="newsletter">Newsletter sponsorship</option>
                <option value="product_launch">Product launch</option>
                <option value="research">Research or report</option>
                <option value="other">Something else</option>
              </select>
            </div>
            <div>
              <label htmlFor="advertising-budget">Budget or timing</label>
              <input
                id="advertising-budget"
                name="budget"
                onChange={(event) => updateField('budget', event.target.value)}
                placeholder="$5k in June"
                type="text"
                value={form.budget}
              />
            </div>
          </div>

          </> : null}
          <div className="Advertising-field">
            <label htmlFor="advertising-notes">{compact ? 'Note' : 'Campaign notes'}</label>
            <textarea
              id="advertising-notes"
              name="notes"
              onChange={(event) => updateField('notes', event.target.value)}
              placeholder="What are you promoting, who should see it, and what would make it successful?"
              rows="5"
              maxLength={3000}
              value={form.notes}
            />
          </div>

          <button
            aria-busy={status.kind === 'submitting'}
            className="Advertising-submit"
            disabled={status.kind === 'submitting'}
            type="submit"
          >
            {status.kind === 'submitting' ? 'Sending...' : compact ? 'Send note' : 'Send advertising note'}
          </button>

          {status.message ? (
            <p
              className={`Advertising-status Advertising-status--${status.kind}`}
              role={status.kind === 'error' ? 'alert' : 'status'}
            >
              {status.message}
            </p>
          ) : null}
        </form>
      </div>
    </section>
  );
}

export default AdvertisingForm;
